import { makeAutoObservable } from "mobx";
import RootStore from "stores/RootStore";

// Re-use types from AddRawMaterialsWorkflowStore
export interface RawMaterial {
  id: number;
  materialCode: string;
  name: string;
  category: 'aggregate' | 'cement' | 'admixture' | 'water' | 'fiber' | 'other';
  unit: string;
  currentQuantity: number;
  reorderPoint: number;
  unitCost: number;
  supplier: string;
  lastUpdated: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  aggregate: 'Aggregates',
  cement: 'Cement & Cite',
  admixture: 'Admixtures',
  water: 'Water',
  fiber: 'Fibers',
  other: 'Other Materials',
};

// Initial mock materials
const INITIAL_MATERIALS: RawMaterial[] = [
  { id: 1, materialCode: 'AGG-SAND', name: 'Sand (Fine Aggregate)', category: 'aggregate', unit: 'tons', currentQuantity: 850, reorderPoint: 200, unitCost: 25, supplier: 'ABC Aggregates', lastUpdated: '2024-12-09' },
  { id: 2, materialCode: 'AGG-GRAVEL', name: 'Gravel (Coarse Aggregate)', category: 'aggregate', unit: 'tons', currentQuantity: 620, reorderPoint: 150, unitCost: 30, supplier: 'ABC Aggregates', lastUpdated: '2024-12-09' },
  { id: 3, materialCode: 'AGG-STONE', name: '3/4" Crushed Stone', category: 'aggregate', unit: 'tons', currentQuantity: 480, reorderPoint: 100, unitCost: 35, supplier: 'Rocky Mountain Supply', lastUpdated: '2024-12-08' },
  { id: 4, materialCode: 'CEM-T1', name: 'Portland Cement Type I', category: 'cement', unit: 'tons', currentQuantity: 125, reorderPoint: 50, unitCost: 120, supplier: 'Lehigh Cement', lastUpdated: '2024-12-09' },
  { id: 5, materialCode: 'CEM-T2', name: 'Portland Cement Type II', category: 'cement', unit: 'tons', currentQuantity: 85, reorderPoint: 30, unitCost: 125, supplier: 'Lehigh Cement', lastUpdated: '2024-12-07' },
  { id: 6, materialCode: 'CEM-SLAG', name: 'Slag Cement', category: 'cement', unit: 'tons', currentQuantity: 45, reorderPoint: 20, unitCost: 95, supplier: 'Lehigh Cement', lastUpdated: '2024-12-06' },
  { id: 7, materialCode: 'ADM-AEA', name: 'Air Entraining Agent', category: 'admixture', unit: 'gallons', currentQuantity: 250, reorderPoint: 50, unitCost: 15, supplier: 'BASF Admixtures', lastUpdated: '2024-12-09' },
  { id: 8, materialCode: 'ADM-WR', name: 'Water Reducer', category: 'admixture', unit: 'gallons', currentQuantity: 180, reorderPoint: 40, unitCost: 22, supplier: 'BASF Admixtures', lastUpdated: '2024-12-08' },
  { id: 9, materialCode: 'ADM-RET', name: 'Retarder', category: 'admixture', unit: 'gallons', currentQuantity: 120, reorderPoint: 30, unitCost: 18, supplier: 'Sika Corporation', lastUpdated: '2024-12-07' },
  { id: 10, materialCode: 'ADM-ACC', name: 'Accelerator', category: 'admixture', unit: 'gallons', currentQuantity: 95, reorderPoint: 25, unitCost: 20, supplier: 'Sika Corporation', lastUpdated: '2024-12-05' },
  { id: 11, materialCode: 'ADM-SP', name: 'Superplasticizer', category: 'admixture', unit: 'gallons', currentQuantity: 75, reorderPoint: 20, unitCost: 45, supplier: 'BASF Admixtures', lastUpdated: '2024-12-09' },
  { id: 12, materialCode: 'FIB-SYN', name: 'Synthetic Fiber', category: 'fiber', unit: 'lbs', currentQuantity: 500, reorderPoint: 100, unitCost: 8, supplier: 'Fiber Solutions', lastUpdated: '2024-12-04' },
  { id: 13, materialCode: 'FIB-STL', name: 'Steel Fiber', category: 'fiber', unit: 'lbs', currentQuantity: 350, reorderPoint: 75, unitCost: 12, supplier: 'Fiber Solutions', lastUpdated: '2024-12-03' },
];

class MaterialInventoryStore {
  rootStore: RootStore;

  materials: RawMaterial[] = [...INITIAL_MATERIALS];
  searchQuery = '';
  selectedCategory = 'all';
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Computed: Filtered materials
  get filteredMaterials(): RawMaterial[] {
    let result = this.materials;

    if (this.selectedCategory !== 'all') {
      result = result.filter(m => m.category === this.selectedCategory);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(m =>
        m.name.toLowerCase().includes(query) ||
        m.materialCode.toLowerCase().includes(query) ||
        m.supplier.toLowerCase().includes(query)
      );
    }

    return result;
  }

  // Computed: Materials by category
  get materialsByCategory(): Record<string, RawMaterial[]> {
    const grouped: Record<string, RawMaterial[]> = {};
    this.filteredMaterials.forEach(m => {
      if (!grouped[m.category]) grouped[m.category] = [];
      grouped[m.category].push(m);
    });
    return grouped;
  }

  // Computed: Low stock materials
  get lowStockMaterials(): RawMaterial[] {
    return this.materials.filter(m => m.currentQuantity <= m.reorderPoint);
  }

  // Computed: Stats
  get stats() {
    return {
      totalMaterials: this.materials.length,
      lowStock: this.lowStockMaterials.length,
      totalValue: this.materials.reduce((sum, m) => sum + (m.currentQuantity * m.unitCost), 0),
      categories: Object.keys(CATEGORY_LABELS).filter(cat =>
        this.materials.some(m => m.category === cat)
      ).length,
    };
  }

  get categoryLabels() {
    return CATEGORY_LABELS;
  }

  // Actions
  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setSelectedCategory = (category: string) => {
    this.selectedCategory = category;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.searchQuery = '';
    this.selectedCategory = 'all';
    this.loading = false;
    this.error = null;
  };
}

export default MaterialInventoryStore;
